import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Package, ShoppingBag, Clock, Users, ArrowUpRight, AlertTriangle, ImageIcon, Camera, Tag } from "lucide-react";

const Kpi = ({ label, value, icon: Icon, testid }) => (
    <div className="border border-border p-6 relative brutal-card" data-testid={testid}>
        <div className="flex items-start justify-between">
            <div className="overline text-neutral-500">{label}</div>
            <Icon className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
        </div>
        <div className="font-display text-5xl mt-4 tracking-tight leading-none">{value}</div>
    </div>
);

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [dangerBusy, setDangerBusy] = useState("");
    const [repairing, setRepairing] = useState(false);

    const reload = () => {
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/orders").then((r) => setOrders(r.data.slice(0, 5))).catch(() => {});
    };
    useEffect(() => { reload(); }, []);

    const [repairState, setRepairState] = useState(null);

    // Poll repair status while a job is running
    useEffect(() => {
        if (!repairing) return;
        let stopped = false;
        const tick = async () => {
            try {
                const r = await api.get("/admin/repair-status");
                if (stopped) return;
                setRepairState(r.data);
                if (!r.data.running) {
                    setRepairing(false);
                    toast.success(`Repair done · ${r.data.fixed} fixed, ${r.data.already_ok} were fine, ${r.data.still_broken} unresolved`);
                    reload();
                    return;
                }
                setTimeout(tick, 2500);
            } catch (e) {
                setRepairing(false);
            }
        };
        tick();
        return () => { stopped = true; };
    }, [repairing]);

    const runRepair = async () => {
        try {
            const r = await api.post("/admin/repair-images");
            if (r.data.already_running) {
                toast.info("A repair job is already running");
            } else {
                toast.success("Repair started — this runs in the background (may take a few minutes)");
            }
            setRepairing(true);
        } catch (e) {
            toast.error(`Failed to start: ${e?.response?.data?.detail || e?.message}`);
        }
    };

    // On mount, check if a repair job is already in progress
    useEffect(() => {
        api.get("/admin/repair-status")
            .then((r) => { if (r.data.running) { setRepairState(r.data); setRepairing(true); } })
            .catch(() => {});
    }, []);

    // Photo Worker Widget — polls the background photo-fetcher every 5s
    const [photoWorker, setPhotoWorker] = useState(null);
    const [needsCategory, setNeedsCategory] = useState(0);
    const [needsUpload, setNeedsUpload] = useState(0);
    useEffect(() => {
        let stopped = false;
        const tick = async () => {
            try {
                const [ws, ss] = await Promise.all([
                    api.get("/admin/photo-worker/status"),
                    api.get("/products/summary"),
                ]);
                if (!stopped) {
                    setPhotoWorker(ws.data);
                    setNeedsCategory(ss.data?.uncategorised ?? 0);
                    setNeedsUpload(ss.data?.needs_upload ?? 0);
                }
            } catch (e) { /* silent */ }
            if (!stopped) setTimeout(tick, 5000);
        };
        tick();
        return () => { stopped = true; };
    }, []);

    const runDanger = async (path, label, confirmText) => {
        const typed = window.prompt(`Type "${confirmText}" to confirm — this is IRREVERSIBLE.`);
        if (typed !== confirmText) return toast.error("Cancelled — confirmation mismatch");
        setDangerBusy(path);
        try {
            const r = await api.post(path);
            toast.success(`${label} · ${JSON.stringify(r.data)}`);
            reload();
        } catch (e) {
            toast.error(`${label} failed: ${e?.response?.data?.detail || e?.message}`);
        } finally {
            setDangerBusy("");
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-10 max-w-7xl">
            <div className="flex items-end justify-between mb-10">
                <div>
                    <div className="overline text-neutral-500">Overview</div>
                    <h1 className="font-display text-5xl tracking-tight leading-none mt-2">Dashboard</h1>
                </div>
                <Link
                    to="/admin/add"
                    className="inline-flex items-center h-11 px-5 bg-black text-white uppercase tracking-widest text-xs font-semibold hover:bg-neutral-800 transition-colors group"
                    data-testid="dashboard-add-product-btn"
                >
                    Add Product <ArrowUpRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5}/>
                </Link>
            </div>

            {/* Photo Worker Widget — live background sync status */}
            {(photoWorker || needsCategory > 0 || needsUpload > 0) ? (
                <div className="mb-6 grid grid-cols-1 lg:grid-cols-4 gap-3" data-testid="dashboard-widgets">
                    {photoWorker ? (
                        <div className="border border-border bg-white flex flex-wrap items-stretch lg:col-span-3" data-testid="photo-worker-widget">
                            <div className="flex items-center gap-3 px-4 sm:px-5 py-4 border-r border-border w-full sm:w-auto">
                                <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${photoWorker.pending_now > 0 ? "bg-neutral-900" : "bg-green-600"}`}>
                                    <Camera className="h-4 w-4 text-white" strokeWidth={1.5}/>
                                </div>
                                <div className="min-w-0">
                                    <div className="overline text-neutral-500">Photo Sync</div>
                                    <div className="font-body text-xs text-neutral-600 mt-0.5 truncate">
                                        {photoWorker.pending_now > 0
                                            ? <>Fetching · <span className="tabular-nums text-black font-semibold">{photoWorker.current_sku || "…"}</span></>
                                            : "All caught up"}
                                    </div>
                                </div>
                            </div>
                            <div className="px-4 sm:px-5 py-4 border-r border-border">
                                <div className="overline text-neutral-500">Ready</div>
                                <div className="font-display text-2xl tabular-nums mt-1 text-green-700" data-testid="photo-worker-ready">{photoWorker.with_photos_now ?? "—"}</div>
                            </div>
                            <div className="px-4 sm:px-5 py-4 border-r border-border">
                                <div className="overline text-neutral-500">Syncing</div>
                                <div className="font-display text-2xl tabular-nums mt-1" data-testid="photo-worker-pending">{photoWorker.pending_now ?? "—"}</div>
                            </div>
                            <div className="flex-1 min-w-[160px] px-4 sm:px-5 py-4 flex flex-col justify-center">
                                <div className="flex items-center justify-between text-[11px] font-body text-neutral-500 mb-1.5">
                                    <span className="truncate">{photoWorker.with_photos_now}/{(photoWorker.with_photos_now || 0) + (photoWorker.pending_now || 0)} with photos</span>
                                    <span className="tabular-nums">{(photoWorker.with_photos_now || 0) + (photoWorker.pending_now || 0) > 0
                                        ? Math.round((photoWorker.with_photos_now || 0) / ((photoWorker.with_photos_now || 0) + (photoWorker.pending_now || 0)) * 100)
                                        : 0}%</span>
                                </div>
                                <div className="h-1.5 bg-neutral-100 relative">
                                    <div
                                        className={`absolute inset-y-0 left-0 ${photoWorker.pending_now > 0 ? "bg-black animate-pulse" : "bg-green-600"}`}
                                        style={{ width: `${(photoWorker.with_photos_now || 0) + (photoWorker.pending_now || 0) > 0 ? Math.round((photoWorker.with_photos_now || 0) / ((photoWorker.with_photos_now || 0) + (photoWorker.pending_now || 0)) * 100) : 0}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}
                    {needsCategory > 0 && (
                        <Link
                            to="/admin/catalog?uncategorised=1"
                            className="border border-border bg-white px-5 py-4 flex items-center gap-4 hover:bg-neutral-50 transition-colors"
                            data-testid="needs-category-tile"
                        >
                            <div className="w-9 h-9 flex items-center justify-center bg-yellow-400 shrink-0">
                                <Tag className="h-4 w-4 text-black" strokeWidth={1.5}/>
                            </div>
                            <div className="min-w-0">
                                <div className="overline text-neutral-500">Needs Category</div>
                                <div className="font-display text-2xl tabular-nums leading-none mt-1">{needsCategory}</div>
                                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Tap to bulk-assign</div>
                            </div>
                        </Link>
                    )}
                </div>
            ) : null}

            {/* Needs Photo Upload — items the worker gave up on */}
            {needsUpload > 0 && (
                <div className="mb-6" data-testid="needs-upload-strip">
                    <Link
                        to="/admin/catalog?needs_upload=1"
                        className="block border-2 border-red-700 bg-red-50/40 px-5 py-4 flex flex-wrap items-center gap-4 hover:bg-red-50 transition-colors"
                        data-testid="needs-upload-tile"
                    >
                        <div className="w-9 h-9 flex items-center justify-center bg-red-600 shrink-0">
                            <ImageIcon className="h-4 w-4 text-white" strokeWidth={1.5}/>
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="overline text-red-700 font-bold">Needs Photo Upload</div>
                            <div className="font-body text-xs text-neutral-700 mt-0.5">
                                <span className="font-display text-2xl tabular-nums leading-none text-red-700 mr-2">{needsUpload}</span>
                                {photoWorker?.resolver?.resolved > 0 && (
                                    <span className="text-green-700 font-semibold">· {photoWorker.resolver.resolved} auto-fetched so far</span>
                                )}
                                {photoWorker?.resolver?.current_sku ? (
                                    <span className="ml-2">· auto-fetching <span className="font-mono">{photoWorker.resolver.current_sku}</span>…</span>
                                ) : (
                                    <span className="ml-2">— background auto-fetch is running. Drag &amp; drop any card to override.</span>
                                )}
                            </div>
                        </div>
                        <div className="uppercase tracking-widest text-[10px] font-semibold border border-red-700 text-red-700 px-3 h-8 flex items-center hover:bg-red-700 hover:text-white transition-colors">
                            Upload Photos
                        </div>
                    </Link>
                </div>
            )}

            {/* Health strip */}
            <div className="mb-8 border border-border p-4 bg-neutral-50" data-testid="repair-strip">
                <div className="flex flex-wrap items-center gap-3">
                    <ImageIcon className="h-4 w-4 text-neutral-500" strokeWidth={1.5}/>
                    <div className="text-sm font-body text-neutral-700 flex-1 min-w-[200px]">Product images broken? Re-fetch them from the source pages (runs in background).</div>
                    <button
                        type="button"
                        disabled={repairing}
                        onClick={runRepair}
                        data-testid="repair-images-btn"
                        className="border border-black uppercase tracking-widest text-[11px] font-semibold px-4 h-10 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
                    >
                        {repairing ? "Repairing in background…" : "Repair Product Images"}
                    </button>
                </div>
                {repairing && repairState && (
                    <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center justify-between text-xs font-body text-neutral-600 mb-1.5">
                            <span>{repairState.done}/{repairState.total} processed</span>
                            <span className="tabular-nums">
                                <span className="text-green-700">✓ {repairState.fixed} fixed</span>
                                <span className="mx-2">·</span>
                                <span className="text-neutral-500">{repairState.already_ok} ok</span>
                                <span className="mx-2">·</span>
                                <span className="text-red-700">{repairState.still_broken} failed</span>
                            </span>
                        </div>
                        <div className="h-1.5 bg-neutral-200 relative">
                            <div className="absolute inset-y-0 left-0 bg-black" style={{ width: `${repairState.total ? Math.round((repairState.done/repairState.total)*100) : 0}%` }}/>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi testid="kpi-products" label="Total Products" value={stats?.total_products ?? "—"} icon={Package} />
                <Kpi testid="kpi-orders" label="Total Orders" value={stats?.total_orders ?? "—"} icon={ShoppingBag} />
                <Kpi testid="kpi-pending" label="Pending Orders" value={stats?.pending_orders ?? "—"} icon={Clock} />
                <Kpi testid="kpi-clients" label="Active Clients" value={stats?.total_clients ?? "—"} icon={Users} />
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
                <section className="border border-border">
                    <header className="px-6 py-4 border-b border-border flex items-center justify-between">
                        <div className="overline text-neutral-600">Products per Brand</div>
                    </header>
                    <div className="p-6 space-y-2">
                        {stats?.per_brand?.map((b) => (
                            <div key={b.brand} className="flex items-center gap-4">
                                <div className="w-32 font-body text-sm">{b.brand}</div>
                                <div className="flex-1 h-2 bg-neutral-100 relative">
                                    <div
                                        className="absolute top-0 left-0 h-full bg-black"
                                        style={{ width: `${Math.min(100, (b.count / Math.max(1, stats.total_products)) * 100)}%` }}
                                    />
                                </div>
                                <div className="font-body text-xs tabular-nums w-8 text-right">{b.count}</div>
                            </div>
                        ))}
                        {!stats?.per_brand?.length && <div className="text-sm text-neutral-500 font-body">No brands yet</div>}
                    </div>
                </section>

                <section className="border border-border">
                    <header className="px-6 py-4 border-b border-border flex items-center justify-between">
                        <div className="overline text-neutral-600">Recent Orders</div>
                        <Link to="/admin/orders" className="text-xs uppercase tracking-widest text-neutral-600 hover:text-black" data-testid="dashboard-view-all-orders">View all</Link>
                    </header>
                    <div className="divide-y divide-border">
                        {orders.length === 0 && <div className="p-6 text-sm text-neutral-500 font-body">No orders yet</div>}
                        {orders.map((o) => (
                            <div key={o.id} className="p-5 flex items-center justify-between hover:bg-neutral-50 transition-colors">
                                <div>
                                    <div className="font-mono text-xs text-neutral-500">{o.order_number}</div>
                                    <div className="font-body text-sm mt-1">{o.client_company || o.client_name} · <span className="text-neutral-500">ID {o.client_login_id}</span></div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display text-lg tabular-nums">£{o.total.toFixed(2)}</div>
                                    <div className="overline mt-1 text-neutral-500">{o.status}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            {/* Danger Zone — admin master only */}
            <section className="mt-12 border-2 border-red-700 bg-red-50/40" data-testid="danger-zone">
                <header className="px-6 py-4 border-b-2 border-red-700 flex items-center gap-3">
                    <AlertTriangle className="h-4 w-4 text-red-700" strokeWidth={1.5}/>
                    <div className="overline text-red-700 font-bold">Danger Zone · Irreversible</div>
                </header>
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        type="button"
                        disabled={!!dangerBusy}
                        onClick={() => runDanger("/admin/danger/wipe-catalog", "Catalog wiped", "WIPE CATALOG")}
                        className="border border-red-700 text-red-700 uppercase tracking-widest text-[11px] font-semibold px-4 py-3 hover:bg-red-700 hover:text-white transition-colors disabled:opacity-50"
                        data-testid="danger-wipe-catalog"
                    >
                        {dangerBusy === "/admin/danger/wipe-catalog" ? "Wiping…" : "Wipe Entire Catalog"}
                    </button>
                    <button
                        type="button"
                        disabled={!!dangerBusy}
                        onClick={() => runDanger("/admin/danger/wipe-orders", "Orders wiped", "WIPE ORDERS")}
                        className="border border-red-700 text-red-700 uppercase tracking-widest text-[11px] font-semibold px-4 py-3 hover:bg-red-700 hover:text-white transition-colors disabled:opacity-50"
                        data-testid="danger-wipe-orders"
                    >
                        {dangerBusy === "/admin/danger/wipe-orders" ? "Wiping…" : "Wipe Every Order"}
                    </button>
                    <button
                        type="button"
                        disabled={!!dangerBusy}
                        onClick={() => runDanger("/admin/danger/wipe-test-clients", "Test clients removed", "REMOVE TEST CLIENTS")}
                        className="border border-red-700 text-red-700 uppercase tracking-widest text-[11px] font-semibold px-4 py-3 hover:bg-red-700 hover:text-white transition-colors disabled:opacity-50"
                        data-testid="danger-wipe-test-clients"
                    >
                        {dangerBusy === "/admin/danger/wipe-test-clients" ? "Removing…" : "Remove Test Clients"}
                    </button>
                </div>
                <div className="px-6 pb-5 text-[11px] font-body text-neutral-600">
                    Only the owner (<code className="bg-white px-1">7861</code>) can see this. Wiping the catalog also resets SKU numbering to <code className="bg-white px-1">AVC-000001</code>. Test-clients wipe keeps <code className="bg-white px-1">7861</code> only (personal-only mode).
                </div>
            </section>
        </div>
    );
}
